<template>
  <el-form
    class="form-step-1"
    ref="form"
    :model="model"
    :rules="rules"
    @keypress.enter.native="goToNextStep"
  >
    <el-row :gutter="16">
      <el-col :lg="24" :md="24" :sm="24" :xs="24">
        <el-form-item
          class="el-form-item--align-center"
          :prop="fields.clubLogo.name"
          :required="fields.clubLogo.required"
        >
          <app-image-upload
            v-model="clubLogo"
            :max="fields.clubLogo.max"
            :path="fields.clubLogo.path(model.id)"
            :placeholder="`${i18n('entities.teamRegistration.placeholders.clubLogo')} *`"
            :schema="fields.clubLogo.fileSchema"
            @success="validateClubLogo"
          />
        </el-form-item>
        <br />
      </el-col>

      <el-col :lg="12" :md="12" :sm="12" :xs="24">
        <el-form-item
          :label="fields.clubName.label"
          :prop="fields.clubName.name"
          :required="fields.clubName.required"
        >
          <el-input
            v-model="clubName"
            :name="fields.clubName.name"
            prefix-icon="icon-teams"
            :placeholder="i18n('entities.teamRegistration.placeholders.clubName')"
          />
        </el-form-item>
      </el-col>

      <el-col :lg="12" :md="12" :sm="12" :xs="24">
        <el-form-item
          :label="fields.clubLegalName.label"
          :prop="fields.clubLegalName.name"
          :required="fields.clubLegalName.required"
        >
          <el-input
            v-model="clubLegalName"
            :name="fields.clubLegalName.name"
            prefix-icon="icon-teams"
            :placeholder="i18n('entities.teamRegistration.placeholders.clubLegalName')"
          />
        </el-form-item>
      </el-col>

      <el-col :lg="12" :md="12" :sm="12" :xs="24">
        <el-form-item
          :label="fields.clubType.label"
          :prop="fields.clubType.name"
          :required="fields.clubType.required"
        >
          <el-select
            v-model="clubType"
            :placeholder="i18n('entities.teamRegistration.placeholders.clubType')"
          >
            <i class="icon-teams" slot="prefix" />
            <el-option
              :key="option.id"
              :label="option.label"
              :value="option.id"
              v-for="option in fields.clubType.options"
            />
          </el-select>
        </el-form-item>
      </el-col>

      <el-col
        v-if="clubType === 'federated'"
        :lg="12" :md="12" :sm="12" :xs="24"
      >
        <el-form-item
          :label="fields.clubFederation.label"
          :prop="fields.clubFederation.name"
          :required="fields.clubFederation.required"
        >
          <el-select
            v-model="clubFederation"
            :placeholder="i18n('entities.teamRegistration.placeholders.clubFederation')"
          >
            <i
              class="icon-location-pin"
              slot="prefix"
            />
            <el-option
              :key="option.id"
              :label="option.label"
              :value="option.id"
              v-for="option in clubFederationOptions"
            />
          </el-select>
        </el-form-item>
      </el-col>

      <el-col :lg="12" :md="12" :sm="12" :xs="24">
        <el-form-item
          :label="fields.clubLegalEntityId.label"
          :prop="fields.clubLegalEntityId.name"
          :required="fields.clubLegalEntityId.required"
        >
          <el-input
            v-model="clubLegalEntityId"
            :name="fields.clubLegalEntityId.name"
            prefix-icon="icon-album"
            :placeholder="i18n('entities.teamRegistration.placeholders.clubLegalEntityId')"
          />
        </el-form-item>
      </el-col>

      <el-col :lg="12" :md="12" :sm="12" :xs="24">
        <el-form-item
          :label="fields.clubWebsite.label"
          :prop="fields.clubWebsite.name"
          :required="fields.clubWebsite.required"
        >
          <el-input
            v-model="clubWebsite"
            :name="fields.clubWebsite.name"
            prefix-icon="icon-link"
            :placeholder="i18n('entities.teamRegistration.placeholders.clubWebsite')"
          />
        </el-form-item>
      </el-col>
    </el-row>

    <el-row :gutter="16">
      <el-col :lg="24" :md="24" :sm="24" :xs="24">
        <app-form-subtitle :caption="`${fields.clubNeedsAccommodation.label} *`" />
      </el-col>
      <el-form-item
        :prop="fields.clubNeedsAccommodation.name"
        :required="fields.clubNeedsAccommodation.required"
      >
        <el-radio
          v-for="option in fields.clubNeedsAccommodation.options"
          :key="option.id"
          :label="option.id"
          v-model="clubNeedsAccommodation"
        >
          <span v-text="option.label" />
        </el-radio>
      </el-form-item>
    </el-row>

    <el-row :gutter="16">
      <el-col :lg="24" :md="24" :sm="24" :xs="24">
        <app-form-subtitle
          i18nCode="teamRegistrationSite.form.step1.subtitles.homeKitColor"
          :required="fields.clubKitHome.required"
        />
      </el-col>
      <el-form-item
        ref="clubKitHome"
        :prop="fields.clubKitHome.name"
        :required="fields.clubKitHome.required"
      >
        <app-kit-color-picker
          v-model="clubKitHome"
          @clear="onClubKitClear('home')"
        />
      </el-form-item>
    </el-row>

    <el-row :gutter="16">
      <el-col :lg="24" :md="24" :sm="24" :xs="24">
        <app-form-subtitle
          i18nCode="teamRegistrationSite.form.step1.subtitles.awayKitColor"
          :required="fields.clubKitAway.required"
        />
      </el-col>
      <el-form-item
        ref="clubKitAway"
        :prop="fields.clubKitAway.name"
        :required="fields.clubKitAway.required"
      >
        <app-kit-color-picker
          v-model="clubKitAway"
          @clear="onClubKitClear('away')"
        />
      </el-form-item>
    </el-row>
  </el-form>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'

import AppImageUpload from '@/components/ImageUpload.vue'
import AppFormSubtitle from '@/components/FormSubtitle.vue'
import AppKitColorPicker from '@/components/KitColorPicker.vue'

import { i18n } from '@shared/i18n'
import { FormSchema } from '@shared/form/form-schema'

import { TeamRegistrationModel } from '@/models/team-registration-model'

import { generateVuexStateComputed } from '@/helpers/generateVuexStateComputed'

const { fields } = TeamRegistrationModel

const formSchema = new FormSchema([
  fields.clubLogo,
  fields.clubName,
  fields.clubLegalName,
  fields.clubType,
  fields.clubFederation,
  fields.clubLegalEntityId,
  fields.clubWebsite,
  fields.clubNeedsAccommodation,
  fields.clubKitHome,
  fields.clubKitAway,
])

export default {
  name: 'appFormStep1',
  data() {
    return {
      i18n,
      fields,
      rules: formSchema.rules(),
    }
  },
  computed: {
    ...mapGetters('teamRegistration', ['model']),

    clubLogo: generateVuexStateComputed('clubLogo'),
    clubName: generateVuexStateComputed('clubName'),
    clubLegalName: generateVuexStateComputed('clubLegalName'),
    clubType: generateVuexStateComputed('clubType'),
    clubFederation: generateVuexStateComputed('clubFederation'),
    clubLegalEntityId: generateVuexStateComputed('clubLegalEntityId'),
    clubWebsite: generateVuexStateComputed('clubWebsite'),
    clubNeedsAccommodation: generateVuexStateComputed('clubNeedsAccommodation'),
    clubKitHome: generateVuexStateComputed('clubKitHome'),
    clubKitAway: generateVuexStateComputed('clubKitAway'),

    clubFederationOptions() {
      const options = this.fields.clubFederation.options
      return [...options].sort((a, b) => (a.label > b.label) ? 1 : -1);
    },
  },
  async mounted() {
    await this.$nextTick()
    if (!this.$refs.form) return
    this.setCurrentStepValidate(this.$refs.form.validate)
  },
  methods: {
    ...mapActions('form', ['goToNextStep']),
    ...mapMutations('form', ['setCurrentStepValidate']),
    async validateClubLogo() {
      try {
        await this.$nextTick()
        this.$refs.form.validateField(this.fields.clubLogo.name)
      } catch (e) {
        // 
      }
    },
    async onClubKitClear(type) {
      await this.$nextTick()
      const refName = type === 'home'
        ? 'clubKitHome'
        : 'clubKitAway'
      this.$refs[refName].clearValidate()
    },
  },
  watch: {
    clubType(val) {
      if (val !== 'federated') this.clubFederation = null
    },
  },
  components: {
    AppImageUpload,
    AppFormSubtitle,
    AppKitColorPicker,
  },
}
</script>

<style lang="scss">
.form-step-1 {
  .el-form-item {
    &--align-center {
      .el-form-item__content {
        line-height: 1.2;
      }

      .el-form-item__error {
        left: 50%;
        transform: translateX(-50%);
      }
    }
  }
}
.el-select-dropdown {
  .el-scrollbar {
    .el-select-dropdown__wrap {
      .el-select-dropdown__list {
        padding: 0 0 !important;
      }
    }
  }
  .popper__arrow {
    left: calc(50% - 6px) !important;
  }
}
</style>
